import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { translations } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthCtx = createContext(null);

export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "de");
  const [theme, setThemeState] = useState(() => localStorage.getItem("theme") || "light");

  const t = translations[lang] || translations.de;

  // Apply theme class scoped to dashboard only (not landing/login).
  // Theme is stored; components decide scope. We still persist to localStorage.
  useEffect(() => {
    localStorage.setItem("theme", theme);
  }, [theme]);

  const setTheme = useCallback((mode) => setThemeState(mode), []);
  const toggleTheme = useCallback(() => setThemeState((m) => (m === "dark" ? "light" : "dark")), []);

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("lang", l);
    if (user) {
      axios.patch(`${API}/auth/locale`, { locale: l }).catch(() => {});
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    // CRITICAL: skip /me check if we are returning from OAuth callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
      if (r.data.locale) {
        setLangState(r.data.locale);
        localStorage.setItem("lang", r.data.locale);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, lang, setLang, theme, setTheme, toggleTheme, t, logout, API }}>
      {children}
    </AuthCtx.Provider>
  );
}

export { API };
